/* eslint-disable default-param-last */
import CustomError from './CustomError';

type Map = Record<string, string | boolean | number>;
type FetchFunction = (
  input: RequestInfo,
  init?: RequestInit
) => Promise<Response>;

const stringifyMap = (map: Map) => Object.entries(map)
  .map(([key, value]) => [key, `${<string>value}`])
  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

export default class HttpClient {
  private apiUri: string;

  private defaultHeaders: Map;

  private altfetch?: string;

  constructor(apiUri: string, defaultHeaders: Map = {}, altfetch?: string) {
    this.apiUri = apiUri;
    this.defaultHeaders = defaultHeaders;
    this.altfetch = altfetch;
  }

  async get<TResponse>(
    path: string,
    query = {},
    headers = {},
  ): Promise<TResponse> {
    const headersExtended = {
      Accept: 'application/json; charset=utf-8',
      ...headers,
    };
    return this.fetch<undefined, TResponse>(
      'GET',
      path,
      query,
      headersExtended,
    );
  }

  async post<TBody>(
    path: string,
    body: TBody,
    query: Map = {},
    headers: Map = {},
  ): Promise<void> {
    const headersExtended = {
      'Content-Type': 'application/json; charset=utf-8',
      ...headers,
    };
    return this.fetch<TBody, void>('POST', path, query, headersExtended, body);
  }

  async fetch<TBody, TResponse>(
    method: 'POST' | 'GET',
    path: string,
    query: Map = {},
    headers: Map = {},
    body?: TBody,
  ): Promise<TResponse> {
    const params = new URLSearchParams(
      Object.entries(query).map(([key, value]) => [key, value.toString()]),
    );
    const qs = params.toString();
    const qsPrefixed = qs ? `?${qs}` : '';
    // @ts-expect-error altfetch can be undefined
    const afetch: FetchFunction = (window[this.altfetch]
      ?? fetch) as FetchFunction;
    const fullPath = `${this.apiUri}${path}${qsPrefixed}`;
    const response = await afetch(fullPath, {
      method,
      headers: stringifyMap({
        ...this.defaultHeaders,
        ...headers,
      }),
      mode: 'cors',
      credentials: 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      const errorBody = (await response.json()) as {
        error: string;
        message: string;
        statusCode: number;
      };
      const { error: statusText, message, statusCode } = errorBody;
      throw new CustomError({
        statusText,
        message,
        statusCode,
      });
    }
    return method === 'GET' ? response.json() as unknown as TResponse : undefined;
  }
}
